import { AgGridAngular } from '@ag-grid-community/angular';
import { GridApi, GridOptions, GridReadyEvent, IRowNode, RowNode } from '@ag-grid-community/core';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import dayjs from 'dayjs';
import { groupBy } from 'lodash-es';
import { ReplaySubject } from 'rxjs';

import { MilestoneQuery } from '@models/milestone-category/milestone/milestone.query';
import { TimelineQuery } from '@models/milestone-category/milestone/timeline.query';
import { TimelineService } from '@models/milestone-category/milestone/timeline.service';

import { AgCheckboxRendererComponent } from '@shared/ag-components/ag-checkbox-rerender/ag-checkbox-renderer.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { EMPTY_UUID } from '@shared/constants/uuid';
import { listTimelineMilestonesQuery } from '@shared/services/gql.service';
import { batchPromises } from '@shared/utils';
import { Utils } from '@shared/utils/utils';

import { TimelineDialogFormValue } from '../timeline-dialog/timeline-dialog.model';

interface TimelineDependencyGridData {
  accept: boolean;
  current_end_date: string;
  current_start_date: string;
  milestone_id: string;
  months: string;
  name: string;
  query: listTimelineMilestonesQuery;
  revised_end_date: Date;
  revised_start_date: Date;
  timeline_milestone_id: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AgGridAngular],
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
  template: `
    <div class="w-screen max-w-3xl font-inter">
      <div class="mb-4 text-xs text-aux-error">Not applying will remove the dependency</div>

      <div class="mb-12">
        <ag-grid-angular
          style="height: 300px"
          class="ag-theme-alpine"
          [rowData]="gridData"
          [gridOptions]="gridOptions"
          (gridReady)="onGridReady($event)"
        />
      </div>
    </div>
  `,
})
export class TimelineDependencyComponent {
  milestoneQuery = inject(MilestoneQuery);

  private timelineQuery = inject(TimelineQuery);
  private timelineService = inject(TimelineService);

  loading = signal(false);

  gridAPI!: GridApi;
  gridAPI$ = new ReplaySubject<GridApi>(1);
  gridData: TimelineDependencyGridData[] = [];
  gridOptions = {
    columnDefs: [
      {
        cellRenderer: AgCheckboxRendererComponent,
        field: 'accept',
        headerName: 'ACCEPT CHANGE?',
        maxWidth: 90,
      },
      {
        field: 'name',
        headerName: 'NAME',
        maxWidth: 150,
      },
      {
        field: 'current_start_date',
        headerName: 'CURRENT START DATE',
        type: 'date',
        valueFormatter: (x) => Utils.dateFormatter(x.value),
      },
      {
        field: 'current_end_date',
        headerName: 'CURRENT END DATE',
        type: 'date',
        valueFormatter: (x) => Utils.dateFormatter(x.value),
      },
      {
        cellClass: ['text-aux-blue'],
        field: 'revised_start_date',
        headerClass: ['text-aux-blue'],
        headerName: 'REVISED START DATE',
        type: 'date',
        valueFormatter: (x) => Utils.dateFormatter(x.value),
      },
      {
        cellClass: ['text-aux-blue'],
        field: 'revised_end_date',
        headerClass: ['text-aux-blue'],
        headerName: 'REVISED END DATE',
        type: 'date',
        valueFormatter: (x) => Utils.dateFormatter(x.value),
      },
    ],
    columnTypes: Utils.columnTypes,
    defaultColDef: {
      resizable: false,
      sortable: false,
      suppressMenu: true,
      suppressMovable: true,
    },
    headerHeight: 40,
    suppressCellFocus: true,
    suppressMenuHide: true,
  } as GridOptions;
  ref = inject<
    CustomOverlayRef<
      unknown,
      {
        field_name: 'actual' | 'contract' | 'revised';
        id: string;
        milestone_id: string;
        newFormValue: TimelineDialogFormValue;
        oldFormValue: TimelineDialogFormValue;
      }
    >
  >(CustomOverlayRef);

  constructor() {
    if (this.ref.data) {
      const { field_name, milestone_id, newFormValue, oldFormValue } = this.ref.data;

      const { items } = this.timelineQuery.getValue();

      const groupedItems = groupBy(items, 'track_from_milestone.id');

      const idsToLookFor = [milestone_id];
      const timelinesToCalculate: listTimelineMilestonesQuery[] = [];

      while (idsToLookFor.length) {
        const item_id = idsToLookFor.shift();

        if (item_id) {
          (groupedItems[item_id] || []).forEach((item) => {
            timelinesToCalculate.push(item);
            idsToLookFor.push(item.milestone.id);
          });
        } else {
          break;
        }
      }

      const t2 = new Date(
        oldFormValue[`${field_name}_end_date`] || oldFormValue.contract_end_date
      ).getTime();
      const t1 = new Date(newFormValue[`${field_name}_end_date`] as string).getTime();
      const dateDiff = Math.floor((t1 - t2) / (24 * 3600 * 1000));

      this.gridData = timelinesToCalculate.map((item) => {
        const milestone = this.milestoneQuery.getEntity(item.milestone.id);
        const start_date = (item[`${field_name}_start_date`] || item.contract_start_date) as string;
        const end_date = (item[`${field_name}_end_date`] || item.contract_end_date) as string;

        return {
          accept: true,
          current_end_date: end_date,
          current_start_date: start_date,
          milestone_id: item.milestone.id,
          months: '',
          name: milestone?.name || '',
          query: item,
          revised_end_date: Utils.addDays(Utils.dateParse(end_date), dateDiff),
          revised_start_date: Utils.addDays(Utils.dateParse(start_date), dateDiff),
          timeline_milestone_id: item.id,
        };
      });
    }
  }

  onGridReady({ api }: GridReadyEvent) {
    this.gridAPI$.next(api);
    this.gridAPI = api;
    api.sizeColumnsToFit();
  }

  async onSave() {
    if (this.loading()) {
      return;
    }

    this.loading.set(true);
    const rows: IRowNode[] = [];

    this.gridAPI.forEachNode((row) => rows.push(row));
    const proms = [];
    const inputs = [];

    if (this.ref.data) {
      const { field_name } = this.ref.data;

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const { accept, query, revised_end_date, revised_start_date, timeline_milestone_id } =
          row.data as TimelineDependencyGridData;

        if (accept) {
          const prevRowData = (rows[i - 1] as RowNode | undefined)?.data as
            | TimelineDependencyGridData
            | undefined;

          const start = dayjs(Utils.awsDateFormatter(revised_start_date)).format('YYYY-MM-DD');
          const end = dayjs(Utils.awsDateFormatter(revised_end_date)).format('YYYY-MM-DD');

          proms.push(
            // if accepted just change the revised dates.
            this.timelineService.updateTimelineForDependency({
              contract_end_date: end,
              contract_start_date: start,
              field_name,
              id: timeline_milestone_id,
              track_from_milestone_id:
                prevRowData && !prevRowData.accept
                  ? EMPTY_UUID
                  : query.track_from_milestone?.id || null,
            })
          );
          inputs.push({
            contract_end_date: end,
            contract_start_date: start,
            field_name,
            id: timeline_milestone_id,
            track_from_milestone_id:
              prevRowData && !prevRowData.accept
                ? EMPTY_UUID
                : query.track_from_milestone?.id || null,
          });
        } else {
          // TODO test this branch
          proms.push(
            // if didn't accepted remove the track from
            this.timelineService.updateTimelineForDependency({
              contract_end_date: query.contract_end_date,
              contract_start_date: query.contract_start_date,
              field_name,
              id: timeline_milestone_id,
              track_from_milestone_id: EMPTY_UUID,
            })
          );
          inputs.push({
            contract_end_date: query.contract_end_date,
            contract_start_date: query.contract_start_date,
            field_name,
            id: timeline_milestone_id,
            track_from_milestone_id: EMPTY_UUID,
          });
        }
      }

      await batchPromises(proms, (p) => p);
      this.loading.set(false);
      this.ref.close(inputs);
    }
  }
}
